import { useEffect, useState } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "../../i18n/LanguageContext";

export default function Reviews() {
  const { t } = useTranslation();
  const r = t.reviews;
  const total = r.items.length;
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = () => setIdx((i) => (i + 1) % total);
  const prev = () => setIdx((i) => (i - 1 + total) % total);

  useEffect(() => {
    if (paused) return undefined;
    const timer = setInterval(() => setIdx((i) => (i + 1) % total), 6500);
    return () => clearInterval(timer);
  }, [paused, total]);

  useEffect(() => {
    if (idx >= total) setIdx(0);
  }, [idx, total]);

  const review = r.items[idx];

  return (
    <section
      id="velemenyek"
      data-testid="reviews-section"
      className="relative py-16 md:py-32 bg-[#2a162f] overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-16 md:h-28 bg-gradient-to-b from-transparent to-[#ebe2f5]" />
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-[#c5a059] opacity-[0.07] blur-[140px]" />

      <div className="relative max-w-5xl mx-auto px-5 sm:px-6 md:px-12 text-center">
        <p className="text-eyebrow">{r.eyebrow}</p>
        <h2 className="mt-5 font-serif-display text-[34px] leading-[1.1] sm:text-4xl md:text-5xl lg:text-6xl text-white sm:leading-[1.05]">
          {r.titleA}{" "}
          <span className="italic gold-text">{r.titleB}</span>
        </h2>

        <div className="relative mt-14 md:mt-16">
          <Quote
            size={60}
            strokeWidth={1}
            className="text-[#c5a059]/35 mx-auto"
          />

          <div
            data-testid="review-card"
            className="mt-4 min-h-[260px] md:min-h-[300px] flex flex-col items-center"
            key={`${review.name}-${idx}`}
          >
            <div className="flex items-center gap-1 mb-7 fade-up">
              {Array.from({ length: 5 }).map((_, j) => (
                <Star
                  key={j}
                  size={16}
                  className="text-[#c5a059]"
                  fill="#c5a059"
                  strokeWidth={1}
                />
              ))}
            </div>

            <p
              data-testid="review-quote"
              className="font-serif-display text-2xl md:text-3xl lg:text-[34px] text-white leading-[1.35] max-w-3xl fade-up fade-up-d1"
            >
              „{review.quote}”
            </p>

            <div className="mt-10 fade-up fade-up-d2">
              <div className="gold-line mx-auto mb-5" />
              <div
                data-testid="review-name"
                className="font-serif-display text-xl text-white"
              >
                {review.name}
              </div>
              <div className="text-xs uppercase tracking-[0.22em] text-[#d9cde0] mt-1.5">
                {review.role}
              </div>
            </div>
          </div>

          <div className="mt-12 flex items-center justify-center gap-6">
            <button
              data-testid="reviews-prev"
              onClick={prev}
              aria-label={r.prevAria}
              className="w-12 h-12 rounded-full border border-white/25 flex items-center justify-center text-white/80 hover:border-[#c5a059] hover:text-[#c5a059] transition-colors"
            >
              <ChevronLeft size={18} />
            </button>

            <div className="flex items-center gap-2">
              {r.items.map((_, i) => (
                <button
                  key={i}
                  data-testid={`review-dot-${i}`}
                  onClick={() => setIdx(i)}
                  aria-label={`${i + 1}`}
                  className={`h-[3px] transition-all duration-500 ${
                    i === idx
                      ? "w-10 bg-[#c5a059]"
                      : "w-4 bg-white/20 hover:bg-white/35"
                  }`}
                />
              ))}
            </div>

            <button
              data-testid="reviews-next"
              onClick={next}
              aria-label={r.nextAria}
              className="w-12 h-12 rounded-full border border-white/25 flex items-center justify-center text-white/80 hover:border-[#c5a059] hover:text-[#c5a059] transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
